import React, {useMemo} from 'react';
import {ComboBox, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import type {PortalEligibleApprover} from '../../hooks/usePortalEligibleApprovers';

type ApproverOption = {id: string; name: string};

export type PortalApproverPickerProps = {
	/**
	 * The selected approver as a portal_users UUID, or null for none. NOT a Forecast
	 * person id and not a Relay global id — sign-off is a client workspace action, so
	 * the approver is a client workspace user.
	 */
	value: string | null;
	/**
	 * The eligible client workspace users for this project, from
	 * `usePortalEligibleApprovers`. Passed in rather than fetched here: both surfaces
	 * that use this picker are LISTS (a row per phase, a row per task), so fetching
	 * internally would be one request per row.
	 */
	approvers: PortalEligibleApprover[];
	/** The list is still in flight — render neither options nor the empty state yet. */
	loading?: boolean;
	/** The list failed to load. Distinguished from "nobody is eligible": see below. */
	error?: boolean;
	disabled?: boolean;
	/**
	 * True when the picker sits on a dark surface, so its empty and error states — which
	 * are prose, not a control — are drawn on-dark rather than in the default body
	 * colour. Nothing infers the surface, and the wrong choice is unreadable rather than
	 * merely off, so the caller has to say. The phase card in the Manage Phases modal is
	 * dark; the task table row is not.
	 */
	onDark?: boolean;
	onChange: (portalUserId: string | null) => void;
	placeholder?: string;
	ariaLabel?: string;
	dataCy?: string;
};

/**
 * Picks the CLIENT WORKSPACE USER who signs a phase or task off.
 *
 * FOR-92 built this as "a thin person picker over the firm's active people", writing a
 * raw Forecast person id. That named a firm employee, so a sign-off request had no
 * client to route to and the approval could never appear in the client's workspace.
 * FOR-525 replaced the population: the options are the client organisation's own
 * workspace users, restricted to the roles that may sign off (Client Executive and
 * Active Collaborator — never a Billing Contact) and to those whose visibility scope
 * actually reaches the project. The backend re-checks all of that on write.
 *
 * Users are labelled by email: `portal_users` has no name column, so an email is the
 * only identifier every workspace user is guaranteed to have, including one who has
 * been invited but never signed in.
 */
const PortalApproverPicker = ({
	value,
	approvers,
	loading = false,
	error = false,
	disabled,
	onDark = false,
	onChange,
	placeholder,
	ariaLabel,
	dataCy,
}: PortalApproverPickerProps): React.JSX.Element => {
	const {formatMessage} = useIntl();

	const approverOptions: ApproverOption[] = useMemo(
		() =>
			approvers.map(approver => ({
				id: approver.portalUserId,
				// The role is appended rather than shown in a second column so the label
				// answers "why can I pick them?" inside the ComboBox's own text search.
				name: approver.roleLabel ? `${approver.email} (${approver.roleLabel})` : approver.email,
			})),
		[approvers]
	);

	/**
	 * An empty dropdown tells a PM nothing — they cannot tell "this client has no one who
	 * can sign off" from "the list is broken". So say which, and where to go:
	 *  - failed load → try again; nothing is asserted about the client's users
	 *  - genuinely nobody → point at client-workspace user provisioning, because that IS
	 *    the fix (invite a Client Executive, or widen an existing user's visibility)
	 * Rendered while `loading` is false only, so a slow fetch never flashes "no users".
	 */
	if (!loading && approverOptions.length === 0) {
		// The test hooks go on the wrapper, not on <Text>. The design-system Text
		// destructures its props and forwards only `data-cy` to the DOM, so a
		// `data-testid` handed to it is silently dropped — and a jest mock that spreads
		// every prop would hide that, passing locally while the real attribute never
		// renders.
		return (
			<span data-testid={'portal-approver-picker-empty'} data-cy={dataCy ? `${dataCy}-empty` : undefined}>
				<Text size={'1'} color={onDark ? 'ondark' : undefined}>
					{formatMessage({
						id: error
							? 'client_portal.signoff_approver_load_failed'
							: 'client_portal.signoff_approver_none_eligible',
					})}
				</Text>
			</span>
		);
	}

	// A persisted approver who is no longer offered (deactivated, re-roled, or moved out
	// of scope) has no option to select, so the ComboBox falls back to the placeholder.
	// That is deliberate: showing a stale name as if it were still valid would hide that
	// this phase now has nobody who can sign it off.
	const selectedId = value != null && approverOptions.some(option => option.id === value) ? value : null;

	return (
		<ComboBox
			defaultItems={approverOptions}
			value={selectedId ?? undefined}
			onChange={(portalUserId: string | null) => onChange(portalUserId || null)}
			disabled={disabled}
			placeholder={placeholder}
			aria-label={ariaLabel}
			width={'flex'}
			data-testid={'portal-approver-picker'}
			data-cy={dataCy}
		>
			{(approver: ApproverOption) => (
				<ComboBox.Item key={approver.id} textValue={approver.name}>
					{approver.name}
				</ComboBox.Item>
			)}
		</ComboBox>
	);
};

export default PortalApproverPicker;
